import React, { useState } from "react";
import { PRIMARY_FIELDS } from "./constants";
import DetailsModal from "./DetailsModal";
import ConfigSection from "./ConfigSection";

const ViewConfigs = ({ configs }) => {
  const [expandedCards, setExpandedCards] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [modalData, setModalData] = useState(null);

  const toggleCard = (login) => {
    setExpandedCards((prev) => ({
      ...prev,
      [login]: !prev[login],
    }));
  };

  const toggleSection = (login, section) => {
    const key = `${login}-${section}`;
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const showModal = (title, data) => {
    setModalData({ title, data });
  };

  const closeModal = () => {
    setModalData(null);
  };

  const getFieldValue = (config, field) => {
    const allConfigs = { ...config.risk_config, ...config.pip_config, ...config.ttl_config };
    return allConfigs[field];
  };

  const getOtherFields = (config, primaryFields) => {
    const allConfigs = { ...config.risk_config, ...config.pip_config, ...config.ttl_config };
    const other = {};
    Object.entries(allConfigs).forEach(([key, value]) => {
      if (!primaryFields.includes(key)) {
        other[key] = value;
      }
    });
    return other;
  };

  if (configs.length === 0) {
    return (
      <div className="text-center text-gray-400 py-8">
        هیچ نتیجه‌ای یافت نشد
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {configs.map((config, index) => {
          const allPrimaryFields = [
            ...PRIMARY_FIELDS.drawdown,
            ...PRIMARY_FIELDS.monitoring,
            ...PRIMARY_FIELDS.pip,
            ...PRIMARY_FIELDS.ttl,
          ];
          const otherFields = getOtherFields(config, allPrimaryFields);
          const isExpanded = expandedCards[config.login];

          return (
            <div key={index} className="bg-gray-700 rounded-lg p-6 space-y-4">
              {/* اطلاعات اصلی */}
              <div className="border-b border-gray-600 pb-4">
                <div className="flex justify-between items-start mb-3">
                  <h5 className="text-lg font-semibold text-white">
                    اطلاعات حساب
                  </h5>
                  <div className="flex gap-2">
                    {Object.keys(otherFields).length > 0 && (
                      <button
                        onClick={() => showModal("سایر تنظیمات", otherFields)}
                        className="text-sm px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white rounded transition"
                      >
                        جزئیات بیشتر
                      </button>
                    )}
                    <button
                      onClick={() => toggleCard(config.login)}
                      className="text-sm px-4 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition"
                    >
                      {isExpanded ? "بستن تنظیمات ▲" : "نمایش تنظیمات ▼"}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <span className="text-gray-400 text-sm block">Login:</span>
                    <p className="text-white font-medium">{config.login}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm block">نام:</span>
                    <p className="text-white font-medium">
                      {config.login_name || "—"}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm block">وضعیت:</span>
                    <p
                      className={`font-medium ${
                        config.is_active === "true"
                          ? "text-green-400"
                          : "text-red-400"
                      }`}
                    >
                      {config.is_active === "true" ? "فعال" : "غیرفعال"}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm block">
                      تاریخ ایجاد:
                    </span>
                    <p className="text-white font-medium text-sm">
                      {new Date(config.created_time).toLocaleString("fa-IR")}
                    </p>
                  </div>
                </div>
              </div>

              {/* تنظیمات */}
              {isExpanded && (
                <div className="space-y-6 pt-2">
                  <ConfigSection
                    title="تنظیمات Drawdown (درصدی)"
                    sectionKey="drawdown"
                    config={config}
                    primaryFields={PRIMARY_FIELDS.drawdown}
                    getFieldValue={getFieldValue}
                    expandedSections={expandedSections}
                    toggleSection={toggleSection}
                    borderColor="border-red-500"
                    bgColor="bg-red-900/30"
                    bgColorHover="bg-red-900/50"
                    textColor="text-red-300"
                    extraFilter={(key) => key.includes("drawdown") || key.includes("breach")}
                    configSource="risk_config"
                  />

                  <ConfigSection
                    title="تنظیمات Monitoring"
                    sectionKey="monitoring"
                    config={config}
                    primaryFields={PRIMARY_FIELDS.monitoring}
                    getFieldValue={getFieldValue}
                    expandedSections={expandedSections}
                    toggleSection={toggleSection}
                    borderColor="border-green-500"
                    bgColor="bg-green-900/30"
                    bgColorHover="bg-green-900/50"
                    textColor="text-green-300"
                    extraFilter={(key) => key.includes("enable") || key.includes("close") || key.includes("monitoring")}
                    configSource="risk_config"
                  />

                  <ConfigSection
                    title="تنظیمات Pip"
                    sectionKey="pip"
                    config={config}
                    primaryFields={PRIMARY_FIELDS.pip}
                    getFieldValue={getFieldValue}
                    expandedSections={expandedSections}
                    toggleSection={toggleSection}
                    borderColor="border-blue-500"
                    bgColor="bg-blue-900/30"
                    bgColorHover="bg-blue-900/50"
                    textColor="text-blue-300"
                    configSource="pip_config"
                  />

                  <ConfigSection
                    title="تنظیمات TTL"
                    sectionKey="ttl"
                    config={config}
                    primaryFields={PRIMARY_FIELDS.ttl}
                    getFieldValue={getFieldValue}
                    expandedSections={expandedSections}
                    toggleSection={toggleSection}
                    borderColor="border-purple-500"
                    bgColor="bg-purple-900/30"
                    bgColorHover="bg-purple-900/50"
                    textColor="text-purple-300"
                    configSource="ttl_config"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modalData && (
        <DetailsModal
          isOpen={true}
          onClose={closeModal}
          title={modalData.title}
          data={modalData.data}
        />
      )}
    </>
  );
};

export default ViewConfigs;